import type { LngLat } from "@/utils/polyline";

/**
 * Device-local recent destinations. Captured every time the user runs a trip
 * so the next time they open the search they can re-pick a previous place
 * without retyping. Cloud sync can layer on later — keep the surface tiny.
 */
export interface RecentSearch {
  id: string;
  /** Short geocoder name (street / business). */
  name: string;
  /** Full geocoder address. */
  place: string;
  coords: LngLat;
  /** Last-used timestamp; newer entries float to the top. */
  usedAt: number;
}

const KEY = "pleasant.recentSearches.v1";
const CAP = 12;

function read(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is RecentSearch =>
        p && typeof p.id === "string" && typeof p.place === "string" &&
        Array.isArray(p.coords) && p.coords.length === 2,
    );
  } catch {
    return [];
  }
}

function write(items: RecentSearch[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* quota / private mode — silently ignore */
  }
  window.dispatchEvent(new CustomEvent("recent-searches-changed"));
}

export function loadRecentSearches(): RecentSearch[] {
  // Newest first.
  return [...read()].sort((a, b) => b.usedAt - a.usedAt);
}

/** Record a destination as "just used". De-dupes by ~30m proximity. */
export function recordRecentSearch(p: { name: string; place: string; coords: LngLat }): void {
  const all = read();
  const [lng, lat] = p.coords;
  // De-dupe by close-enough coords (~30m) — same place re-typed slightly
  // differently shouldn't create duplicates.
  const filtered = all.filter((x) => {
    const dLat = (x.coords[1] - lat) * 111_000;
    const dLng = (x.coords[0] - lng) * 111_000 * Math.cos((lat * Math.PI) / 180);
    return Math.hypot(dLat, dLng) > 30;
  });
  filtered.unshift({
    id: crypto.randomUUID(),
    name: p.name,
    place: p.place,
    coords: p.coords,
    usedAt: Date.now(),
  });
  write(filtered.slice(0, CAP));
}

export function clearRecentSearches(): void {
  write([]);
}
